import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import { AlertTriangle, CheckCircle2, RefreshCw, X, Search, Download } from 'lucide-react';

/**
 * Pointing existing loans at the wallet that can actually repay them.
 *
 * Loans recorded before wallets were named charged "the loan user's own
 * wallet", creating one if needed — so a rider under a fleet, and a rider whose
 * loan sits on a legacy duplicate account, were both charged into a wallet no
 * money ever reaches. The server classifies every active loan and suggests a
 * target; nothing changes until the rows are ticked and applied.
 *
 * What applying does, per the finance decision: the deductions already taken
 * from the wrong wallet are carried over to the right one — the wrong wallet
 * returns to zero and the right one goes that far negative, to be repaid from
 * real earnings. For a duplicate, the loan changes hands and the old account is
 * blocked from loans.
 */

const money = (v: any) => `₦${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const KIND: Record<string, { label: string; cls: string }> = {
    fleet:     { label: 'Fleet rider',      cls: 'bg-purple-100 text-purple-700' },
    duplicate: { label: 'Duplicate account', cls: 'bg-amber-100 text-amber-800' },
    manual:    { label: 'Needs a decision', cls: 'bg-red-100 text-red-700' },
    ok:        { label: 'OK',               cls: 'bg-green-100 text-green-700' },
};

function LoanCleanupPanel({ onApplied }: { onApplied?: () => void }) {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [ticked, setTicked] = useState<Record<number, boolean>>({});
    // For "manual" rows: the target picked by hand, keyed by loan id.
    const [manualTargets, setManualTargets] = useState<Record<number, any>>({});
    const [pickerFor, setPickerFor] = useState<any>(null);       // the row a target is being chosen for
    const [pickerSearch, setPickerSearch] = useState<string>('');
    const [pickerResults, setPickerResults] = useState<any[]>([]);
    const [confirming, setConfirming] = useState<boolean>(false);
    const [applying, setApplying] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);

    /**
     * The report for whoever is not looking at this screen: what the problem
     * was, every account changed and how, what is still waiting. Built from the
     * records the clean-up left, so it can be pulled again at any time.
     */
    const downloadReport = async () => {
        setDownloading(true);
        try {
            const res = await SuperAdminApis.downloadLoanCleanupReport();
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `rider-loan-cleanup-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not build the report');
        } finally {
            setDownloading(false);
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res: AxiosResponse<any> = await SuperAdminApis.getLoanCleanupReview();
            const d = res?.data?.data;
            if (d) {
                setData(d);
                // Everything with a confident suggestion starts ticked; manual rows do not.
                const t: Record<number, boolean> = {};
                (d.loans || []).forEach((l: any) => {
                    if ((l.kind === 'fleet' || l.kind === 'duplicate') || (l.kind === 'ok' && l.needs_confirm)) t[l.loan_id] = true;
                });
                setTicked(t);
                setManualTargets({});
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not load the loan review');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const rows: any[] = data?.loans || [];
    const wrong = useMemo(() => rows.filter((l) => l.kind !== 'ok' || l.needs_confirm), [rows]);
    const fine  = useMemo(() => rows.filter((l) => l.kind === 'ok' && !l.needs_confirm), [rows]);

    const targetOf = (l: any) => (l.kind === 'manual' ? manualTargets[l.loan_id] : l.target) || null;

    const selected = useMemo(
        () => wrong.filter((l) => ticked[l.loan_id] && targetOf(l)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [wrong, ticked, manualTargets],
    );

    const carriedTotal = selected.reduce((s, l) => s + Number(l.phantom_balance || 0), 0);

    // ── Manual target picker ────────────────────────────────

    const searchTargets = async (term: string) => {
        setPickerSearch(term);
        if (term.length < 2) { setPickerResults([]); return; }
        try {
            const res: AxiosResponse<any> = await SuperAdminApis.getEligibleLoanRiders(term);
            setPickerResults(res?.data?.data || []);
        } catch {
            setPickerResults([]);
        }
    };

    const chooseTarget = (row: any, r: any) => {
        // A different account → the loan changes hands (duplicate). The same
        // account → only the wallet changes (a fleet rider whose fleet was
        // matched by hand). The server enforces that the wallet is the one the
        // rule would pick for that account either way.
        setManualTargets((m) => ({
            ...m,
            [row.loan_id]: {
                wallet_id: r.wallet_id,
                owner: r.charges,
                owner_email: r.email,
                available: r.wallet_balance,
                earned: r.wallet_earned,
                user_id: r.id !== row.user_id ? r.id : null,
                name: r.name,
            },
        }));
        setTicked((t) => ({ ...t, [row.loan_id]: true }));
        setPickerFor(null);
        setPickerSearch('');
        setPickerResults([]);
    };

    // ── Apply ───────────────────────────────────────────────

    const apply = async () => {
        setApplying(true);
        try {
            const moves = selected.map((l) => {
                const t = targetOf(l);
                return {
                    loan_id: l.loan_id,
                    target_wallet_id: t.wallet_id,
                    target_user_id: t.user_id || undefined,
                    // Decided: a duplicate's old account is always blocked.
                    block_old: !!t.user_id,
                };
            });

            const res: AxiosResponse<any> = await SuperAdminApis.applyLoanCleanup(moves);
            const d = res?.data;

            if (d?.status) {
                toast.success(d.message, { autoClose: 8000 });
                (d.data?.failed || []).forEach((f: any) => toast.warn(`Loan #${f.loan_id}: ${f.error}`, { autoClose: 12000 }));
                setConfirming(false);
                load();
                onApplied?.();
            } else {
                toast.warn(d?.message || 'Could not apply');
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not apply');
        } finally {
            setApplying(false);
        }
    };

    if (!data) {
        return loading ? <div className="bg-white rounded-lg p-4 shadow-sm mb-4 text-sm text-gray-400">Checking loans…</div> : null;
    }

    // Nothing wrong: a single quiet line.
    if (wrong.length === 0) {
        return (
            <div className="bg-white rounded-lg px-4 py-3 shadow-sm mb-4 text-sm text-green-700 flex items-center gap-2">
                <CheckCircle2 size={15} /> Every active loan charges a wallet that receives earnings.
                <button onClick={downloadReport} disabled={downloading}
                    className="ml-auto text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1 disabled:opacity-50">
                    <Download size={12} /> {downloading ? 'Building…' : 'Download clean-up report'}
                </button>
                <button onClick={load} className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-1">
                    <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Re-check
                </button>
            </div>
        );
    }

    const c = data.counts;

    return (
        <div className="bg-white rounded-lg shadow-sm mb-4 border-l-4 border-amber-500">
            <button onClick={() => setOpen((o) => !o)} className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                {/* Only loans that are actually wrong count as wrong; the unconfirmed
                    ones charge the right wallet and just need it written down. */}
                <span className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    {wrong.length - c.unconfirmed > 0
                        ? `${wrong.length - c.unconfirmed} loan(s) are charging the wrong wallet`
                        : `${c.unconfirmed} loan(s) need their wallet confirmed`}
                </span>
                <span className="text-xs text-gray-600">
                    {c.fleet > 0 && <>{c.fleet} fleet rider(s) · </>}
                    {c.duplicate > 0 && <>{c.duplicate} duplicate account(s) · </>}
                    {c.manual > 0 && <>{c.manual} need a decision · </>}
                    {c.unconfirmed > 0 && <>{c.unconfirmed} already correct, to confirm · </>}
                    {money(data.phantom_total)} deducted into wallets that never receive money
                </span>
                <span className="ml-auto text-xs text-[#E04343] font-semibold">{open ? 'Hide' : 'Review and fix'}</span>
            </button>

            {open && (
                <div className="px-4 pb-4">
                    <div className="bg-amber-50 rounded-md px-3 py-2 text-xs text-amber-900 mb-3">
                        <strong>What applying does.</strong> Each ticked loan is pointed at the wallet its rider actually
                        earns into. The deductions already taken from the wrong wallet are <strong>carried over</strong>:
                        that wallet returns to zero and the right one goes that far negative, so the rider repays it from
                        real earnings and the loan's repaid figure stays true. For a duplicate account the loan moves to
                        the earning account and the old account is blocked from loans. Nothing else changes.
                    </div>

                    <div className="border rounded-lg overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-gray-50 text-left text-gray-500 uppercase text-[10px]">
                                <tr>
                                    <th className="px-2 py-2 w-8">
                                        <input type="checkbox"
                                            checked={wrong.every((l) => ticked[l.loan_id] || !targetOf(l))}
                                            onChange={(e) => {
                                                const t: Record<number, boolean> = { ...ticked };
                                                wrong.forEach((l) => { if (targetOf(l)) t[l.loan_id] = e.target.checked; });
                                                setTicked(t);
                                            }} />
                                    </th>
                                    <th className="px-2 py-2">Loan</th>
                                    <th className="px-2 py-2">Problem</th>
                                    <th className="px-2 py-2">Charges now</th>
                                    <th className="px-2 py-2">Will charge</th>
                                    <th className="px-2 py-2 text-right">Carried over</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {wrong.map((l) => {
                                    const t = targetOf(l);
                                    const k = KIND[l.kind] || KIND.manual;
                                    return (
                                        <tr key={l.loan_id} className={ticked[l.loan_id] && t ? 'bg-green-50/40' : ''}>
                                            <td className="px-2 py-2 align-top">
                                                <input type="checkbox" checked={!!ticked[l.loan_id] && !!t} disabled={!t}
                                                    onChange={(e) => setTicked((x) => ({ ...x, [l.loan_id]: e.target.checked }))} />
                                            </td>
                                            <td className="px-2 py-2 align-top">
                                                <div className="font-semibold text-gray-800">{l.rider}</div>
                                                <div className="text-gray-400">{l.email}{l.phone ? ` · ${l.phone}` : ''}</div>
                                                <div className="text-gray-500 mt-0.5">#{l.loan_id} · {money(l.principal)} · {l.cycles} cycles · {money(l.outstanding)} outstanding</div>
                                            </td>
                                            <td className="px-2 py-2 align-top max-w-[18rem]">
                                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${k.cls}`}>
                                                    {l.kind === 'ok' ? 'Correct, unconfirmed' : k.label}
                                                </span>
                                                <p className="text-gray-600 mt-1">{l.reason}</p>
                                            </td>
                                            <td className="px-2 py-2 align-top">
                                                {l.current_wallet ? (
                                                    <>
                                                        <div>{l.current_wallet.owner}'s wallet</div>
                                                        <div className={l.current_wallet.available < 0 ? 'text-[#E04343]' : 'text-gray-500'}>
                                                            {money(l.current_wallet.available)} · earned {money(l.current_wallet.earned)}
                                                        </div>
                                                    </>
                                                ) : <span className="text-gray-400">no wallet</span>}
                                            </td>
                                            <td className="px-2 py-2 align-top">
                                                {t ? (
                                                    <>
                                                        <div className="font-medium text-gray-800">{t.name || t.owner}{t.user_id ? ' (loan moves here)' : ''}</div>
                                                        <div className="text-gray-500">{t.owner_email}</div>
                                                        <div className="text-gray-500">{money(t.available)} · earned {money(t.earned)}</div>
                                                        {l.kind === 'manual' && (
                                                            <button onClick={() => setPickerFor(l)} className="text-[10px] text-[#E04343] underline mt-0.5">change</button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div>
                                                        {(l.candidates || []).length > 0 && (
                                                            <div className="mb-1 space-y-1">
                                                                {l.candidates.map((cand: any) => (
                                                                    <button key={cand.wallet_id}
                                                                        onClick={() => { setManualTargets((m) => ({ ...m, [l.loan_id]: cand })); setTicked((x) => ({ ...x, [l.loan_id]: true })); }}
                                                                        className="block text-left border rounded px-2 py-1 hover:border-gray-500 w-full">
                                                                        <span className="font-medium">{cand.owner}</span> <span className="text-gray-400">{cand.owner_email}</span>
                                                                        <span className="block text-[10px] text-gray-500">matched on {cand.matched_on} · earned {money(cand.earned)}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <button onClick={() => setPickerFor(l)}
                                                            className="text-[11px] bg-gray-800 text-white px-2 py-1 rounded flex items-center gap-1">
                                                            <Search size={11} /> Pick the account
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-2 py-2 align-top text-right whitespace-nowrap">
                                                {Number(l.phantom_balance) > 0
                                                    ? <span className="text-[#E04343] font-medium">−{money(l.phantom_balance)}</span>
                                                    : <span className="text-gray-400">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                        <p className="text-xs text-gray-600">
                            {selected.length} of {wrong.length} selected
                            {carriedTotal > 0 && <> · <strong className="text-[#E04343]">{money(carriedTotal)}</strong> will be carried over to the correct wallets</>}
                            {fine.length > 0 && <> · {fine.length} loan(s) already correct</>}
                        </p>
                        <div className="flex gap-2">
                            <button onClick={downloadReport} disabled={downloading}
                                title="Excel: summary, every change made, what is still outstanding"
                                className="bg-white border text-gray-700 px-3 py-2 rounded text-sm flex items-center gap-1.5 disabled:opacity-50">
                                <Download size={13} /> {downloading ? 'Building…' : 'Download report'}
                            </button>
                            <button onClick={load} disabled={loading} className="bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm flex items-center gap-1.5">
                                <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Re-check
                            </button>
                            <button onClick={() => setConfirming(true)} disabled={!selected.length || applying}
                                className="bg-[#E04343] text-white px-4 py-2 rounded text-sm font-semibold disabled:opacity-50">
                                Fix {selected.length} loan(s)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Pick a target by hand ──────────────────────── */}
            {pickerFor && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <div>
                                <h3 className="font-semibold">Which account repays loan #{pickerFor.loan_id}?</h3>
                                <p className="text-xs text-gray-500">{pickerFor.rider} · {pickerFor.email}</p>
                            </div>
                            <button onClick={() => { setPickerFor(null); setPickerSearch(''); setPickerResults([]); }} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>
                        <div className="px-5 py-4">
                            <input autoFocus value={pickerSearch} onChange={(e) => searchTargets(e.target.value)}
                                placeholder="Search the rider's other account by name, e-mail or phone…"
                                className="w-full border rounded-md px-3 py-2 text-sm" />
                            <p className="text-[11px] text-gray-400 mt-1">Only accounts with an active wallet that has received earnings are listed.</p>
                            <div className="border rounded-md mt-2 max-h-64 overflow-y-auto">
                                {pickerSearch.length < 2 ? (
                                    <p className="text-xs text-gray-400 px-3 py-4 text-center">Type to search.</p>
                                ) : pickerResults.length === 0 ? (
                                    <p className="text-xs text-gray-400 px-3 py-4 text-center">No eligible account matches.</p>
                                ) : pickerResults.map((r: any) => (
                                    <button key={r.id} onClick={() => chooseTarget(pickerFor, r)}
                                        disabled={r.has_active_loan && r.id !== pickerFor.user_id}
                                        className="w-full text-left px-3 py-2 text-xs border-b last:border-b-0 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={r.has_active_loan && r.id !== pickerFor.user_id ? 'That account already has an active loan' : ''}>
                                        <div className="flex justify-between gap-2">
                                            <span className="font-medium">{r.name} <span className="text-gray-400 font-normal">{r.email}{r.phone ? ` · ${r.phone}` : ''}</span></span>
                                            {r.id === pickerFor.user_id && <span className="text-[10px] text-blue-600">same account</span>}
                                        </div>
                                        <div className="text-gray-500 mt-0.5">Charges the {r.charges} · {money(r.wallet_balance)} · earned {money(r.wallet_earned)}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Confirm ────────────────────────────────────── */}
            {confirming && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <h3 className="font-semibold">Fix {selected.length} loan(s)?</h3>
                            <button onClick={() => setConfirming(false)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>
                        <div className="px-5 py-4 text-sm text-gray-700 space-y-3">
                            <ul className="space-y-1 max-h-56 overflow-y-auto text-xs">
                                {selected.map((l) => {
                                    const t = targetOf(l);
                                    return (
                                        <li key={l.loan_id} className="border rounded px-2 py-1.5">
                                            <strong>{l.rider}</strong> → {t.name || t.owner}{t.user_id ? ' (loan moves to this account; old account blocked)' : ''}
                                            {Number(l.phantom_balance) > 0 && <span className="text-[#E04343]"> · carries over {money(l.phantom_balance)}</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                            {carriedTotal > 0 && (
                                <p className="bg-amber-50 border-l-4 border-amber-500 rounded-r px-3 py-2 text-xs text-amber-900">
                                    <strong>{money(carriedTotal)}</strong> in total moves onto the correct wallets as a negative balance.
                                    Those riders earn nothing until it is covered. The loan wallet's figures do not change.
                                </p>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-3 border-t">
                            <button onClick={() => setConfirming(false)} disabled={applying} className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm">Cancel</button>
                            <button onClick={apply} disabled={applying} className="bg-[#E04343] text-white px-4 py-2 rounded text-sm font-semibold disabled:opacity-60">
                                {applying ? 'Applying…' : 'Apply'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LoanCleanupPanel;
